import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { ProgramLesson } from './types';

interface LessonSavedFlash {
  flashLesson: (lessonId: number) => void;
  flashedLessonId: number | null;
}

const LessonSavedFlashContext = createContext<LessonSavedFlash>({
  flashLesson: () => {},
  flashedLessonId: null,
});

// Which lesson row should play the saved pulse (.spa-saved-flash). The page provides this so
// deep cell editors can flash their row, which SPADataTable renders via rowClassName.
export const LessonSavedFlashProvider = ({ children }: { children: React.ReactNode }) => {
  const [flashedLessonId, setFlashedLessonId] = useState<number | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Stable so the memoized context value only changes when the flashed id does.
  const flashLesson = useCallback((lessonId: number) => {
    // Drop the class for a frame so a repeat save on the same row restarts the animation.
    setFlashedLessonId(null);
    requestAnimationFrame(() => setFlashedLessonId(lessonId));
    window.clearTimeout(timer.current);
    // Outlives the 2.2s animation so removing the class never clips the fade.
    timer.current = window.setTimeout(() => setFlashedLessonId(null), 2400);
  }, []);

  const value = useMemo(() => ({ flashLesson, flashedLessonId }), [flashLesson, flashedLessonId]);

  return (
    <LessonSavedFlashContext.Provider value={value}>
      {children}
    </LessonSavedFlashContext.Provider>
  );
};

export const useLessonSavedFlash = () => useContext(LessonSavedFlashContext);

export const savedFlashRowClass = (flashedLessonId: number | null) => (
  (lesson: ProgramLesson) => (lesson.id === flashedLessonId ? 'spa-saved-flash' : '')
);
